import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import ProductModal from "@/components/organisms/purchases/modals/ProductModal";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";

import { useDisclosure } from "@shared/hooks/useDisclosure";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import React from "react";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";

export interface AttractionProductItemProps {
  product: ProductTypeSerializer;
  venue: NestedPublicVenueSerializer;
  isLoading: boolean;
  isBasketUpdating: boolean;
}

const AttractionProductItem: React.FC<AttractionProductItemProps> = ({
  product,
  venue,
  isLoading,
  isBasketUpdating,
}) => {
  const { t } = useTranslation("purchases");
  const defaultImage = formatRelativeStaticPath(
    "images/default-showpass-square.png"
  );
  const skeletonText = "skeletonText";

  const cardImage = product?.image;

  // attraction modal variables
  const {
    isOpen: isOpenProductModal,
    onOpen: onOpenProductModal,
    onClose: onCloseProductModal,
  } = useDisclosure();

  const productName = product?.name ?? t("product");

  // Calculate the price range string, which will be used for both the visible text and the aria-label
  const priceRange =
    product?.product_attributes && product.product_attributes.length > 0
      ? getPriceRange({
          items: product.product_attributes,
          allInPricing: Boolean(venue.all_in_pricing_is_enabled),
          venueCurrency: venue.currency,
        })
      : null;

  return (
    <>
      {product && product.product_attributes && (
        <ProductModal
          isOpen={isOpenProductModal}
          onClose={onCloseProductModal}
          selectedAttractionProduct={product}
          venue={venue}
          hasAllInPricing={Boolean(venue.all_in_pricing_is_enabled)}
          isBasketUpdating={isBasketUpdating}
        />
      )}
      <Box
        flexGrow={2}
        width="100%"
        height="100%"
        justifyContent="center"
        p="3xs"
        _hover={{
          "& img": {
            opacity: 0.5,
          },
        }}
        onClick={() => {
          if (!isLoading && product && product.product_attributes) {
            onOpenProductModal();
          }
        }}
      >
        <Flex
          cursor="pointer"
          direction="column"
          width="100%"
          maxWidth="100%"
          tabIndex={0}
          as="button"
          textAlign="left"
          aria-label={
            priceRange
              ? t("View details for {{productName}} priced at {{priceRange}}", {
                  productName,
                  priceRange,
                })
              : t("View details for {{productName}}", {
                  productName,
                })
          }
          _focusVisible={{
            boxShadow: "outline",
            outlineOffset: "2px",
          }}
        >
          <Skeleton borderRadius="md" isLoaded={!isLoading}>
            <Flex
              borderRadius="md"
              overflow="hidden"
              width="100%"
              height="100%"
              display="block"
            >
              <CardImage
                src={cardImage ?? defaultImage}
                alt={t("Product image")}
                imageStyle={{ width: "100%", height: "100%" }}
                height={190}
                width={190}
              />
            </Flex>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="xs">
            <Text
              fontWeight="bold"
              fontSize={{ base: "sm", lg: "xl" }}
              color="fg"
              lineClamp={2}
            >
              {product?.name ?? skeletonText}
            </Text>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="xs">
            {product?.product_attributes &&
              product.product_attributes.length > 0 && (
                <Text fontSize={{ base: "xs", lg: "lg" }} color="fg">
                  {getPriceRange({
                    items: product.product_attributes,
                    allInPricing: Boolean(venue.all_in_pricing_is_enabled),
                    venueCurrency: venue.currency,
                  }) ?? skeletonText}
                </Text>
              )}
          </Skeleton>
        </Flex>
      </Box>
    </>
  );
};

export default AttractionProductItem;
